import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { X, CheckCircle, Info, AlertCircle, AlertTriangle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from "@/lib/utils"

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed bottom-0 left-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-3 p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
      className
    )}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-start overflow-hidden rounded-2xl border border-white/20 dark:border-white/10 shadow-lg backdrop-blur-sm transition-all will-change-transform",
  {
    variants: {
      variant: {
        default: "bg-white/90 dark:bg-slate-900/90 text-slate-900 dark:text-slate-100 p-4",
        success: "bg-gradient-to-br from-emerald-50/95 to-green-100/95 dark:from-emerald-900/50 dark:to-green-900/50 border-emerald-200/50 dark:border-emerald-700/30 text-emerald-900 dark:text-emerald-100 p-4",
        error: "bg-gradient-to-br from-red-50/95 to-rose-100/95 dark:from-red-900/50 dark:to-rose-900/50 border-red-200/50 dark:border-red-700/30 text-red-900 dark:text-red-100 p-4",
        destructive: "bg-gradient-to-br from-red-50/95 to-red-100/95 dark:from-red-900/50 dark:to-red-900/50 border-red-300/50 dark:border-red-600/30 text-red-900 dark:text-red-100 p-4",
        info: "bg-gradient-to-br from-blue-50/95 to-indigo-100/95 dark:from-blue-900/50 dark:to-indigo-900/50 border-blue-200/50 dark:border-blue-700/30 text-blue-900 dark:text-blue-100 p-4",
        warning: "bg-gradient-to-br from-amber-50/95 to-orange-100/95 dark:from-amber-900/50 dark:to-orange-900/50 border-amber-200/50 dark:border-amber-700/30 text-amber-900 dark:text-amber-100 p-4",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// Optimized animation variants for better performance
const toastAnimationVariants = {
  initial: { 
    opacity: 0, 
    y: 20, 
    x: 50,
    scale: 0.96
  },
  animate: { 
    opacity: 1, 
    y: 0, 
    x: 0,
    scale: 1,
    transition: {
      type: "tween",
      ease: [0.25, 0.1, 0.25, 1], // Custom cubic-bezier for smooth feel
      duration: 0.25 // Much faster
    }
  },
  exit: { 
    opacity: 0, 
    x: 50,
    scale: 0.95,
    transition: { 
      duration: 0.2,
      ease: "easeIn"
    } 
  }
}

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      asChild
      {...props}
    >
      <motion.div
        variants={toastAnimationVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className={cn(toastVariants({ variant }), className)}
        style={{ paddingRight: '2.5rem' }}
      >
        {/* Content area - clean and fast */}
        {props.children}
      </motion.div>
    </ToastPrimitives.Root>
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastIcon = ({ variant }: { variant?: "default" | "success" | "error" | "destructive" | "info" | "warning" }) => {
  const iconProps = { className: "w-5 h-5 flex-shrink-0", strokeWidth: 2 }
  
  const iconComponents = {
    success: <CheckCircle {...iconProps} className="text-emerald-600 dark:text-emerald-400" />,
    error: <AlertCircle {...iconProps} className="text-red-600 dark:text-red-400" />,
    destructive: <AlertCircle {...iconProps} className="text-red-600 dark:text-red-400" />,
    info: <Info {...iconProps} className="text-blue-600 dark:text-blue-400" />,
    warning: <AlertTriangle {...iconProps} className="text-amber-600 dark:text-amber-400" />,
    default: null
  }

  const SelectedIcon = iconComponents[variant || 'default']
  
  if (!SelectedIcon) return null

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        duration: 0.15,
        ease: "easeOut"
      }}
      className="flex items-center justify-center w-8 h-8 rounded-full bg-white/40 dark:bg-black/30"
    >
      {SelectedIcon}
    </motion.div>
  )
}

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm px-3 text-sm font-medium transition-all hover:bg-white/20 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    asChild
    {...props}
  >
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.1 }}
      className={cn(
        "absolute right-2 top-2 z-50 rounded-full p-1.5 bg-black/20 dark:bg-white/20 opacity-70 transition-all hover:opacity-100 hover:bg-black/30 dark:hover:bg-white/30 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/50",
        className
      )}
    >
      <X className="h-4 w-4" />
    </motion.button>
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold leading-tight tracking-tight", className)}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm opacity-90 mt-1 leading-relaxed", className)}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>
type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
  ToastIcon,
}
